// Mednos Profil-Panel — gemeinsame Implementierung für alle Apps.
//
// Klick auf das Profil-Badge (unten links) öffnet ein kleines Panel mit den
// Daten des angemeldeten Users (Avatar mit Initialen, Name, Rolle) und einem
// Logout-Button. Das Panel sieht in jeder App identisch aus (fixes dunkles
// Theme, wie das Sidepanel aus app-switcher.js).
//
// Verwendung:
//   import { initProfilePanel } from '../../platform/shared/profile-panel.js'
//   initProfilePanel({
//     badge: '#profileBadge',
//     loadUser: async () => ({ username, role, email? }),  // app-spezifisch
//     onLogout: () => { ... },                             // optional
//     darstellung: true,                                   // optional, s. u.
//   })
//
// `darstellung: true` zeigt den Schalter Hell/Dunkel (seit 22.09.2026). Er
// gilt app-uebergreifend (platform/shared/theme.js, Cookie auf .mednos.eu) und
// ersetzt die frueheren Schalter auf Papers' Dashboard und in Studys
// Einstellungen. Radiology hat keinen hellen Modus und laesst die Option weg.
//
// Stufe, Passwort und Claude-API-Key standen bis 10.09.2026 als Blöcke im Panel
// (nur Study, über die Option `profileApi`). Sie liegen jetzt im Konto-Tab der
// Einstellungen (konto-pane.js), den der Menüpunkt „Einstellungen" öffnet — für
// Admins heißt er „Admin Einstellungen" und trägt zusätzlich die Admin-Tabs.

import { initAdminKonsole } from './admin-konsole.js';
import { initLlmCostsOverlay } from './llm-costs-overlay.js';
import { aktuellesTheme, setzeTheme } from './theme.js';

// Sonne und Mond im Schalter — dieselben Zeichnungen wie an den frueheren
// Schaltern in Study und Papers, damit er vertraut aussieht.
const ICON_SONNE = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12,17c-2.76,0-5-2.24-5-5s2.24-5,5-5,5,2.24,5,5-2.24,5-5,5Zm0-8c-1.65,0-3,1.35-3,3s1.35,3,3,3,3-1.35,3-3-1.35-3-3-3Zm1-5V1c0-.55-.45-1-1-1s-1,.45-1,1v3c0,.55,.45,1,1,1s1-.45,1-1Zm0,19v-3c0-.55-.45-1-1-1s-1,.45-1,1v3c0,.55,.45,1,1,1s1-.45,1-1ZM5,12c0-.55-.45-1-1-1H1c-.55,0-1,.45-1,1s.45,1,1,1h3c.55,0,1-.45,1-1Zm19,0c0-.55-.45-1-1-1h-3c-.55,0-1,.45-1,1s.45,1,1,1h3c.55,0,1-.45,1-1ZM6.71,6.71c.39-.39,.39-1.02,0-1.41l-2-2c-.39-.39-1.02-.39-1.41,0s-.39,1.02,0,1.41l2,2c.2,.2,.45,.29,.71,.29s.51-.1,.71-.29Zm14,14c.39-.39,.39-1.02,0-1.41l-2-2c-.39-.39-1.02-.39-1.41,0s-.39,1.02,0,1.41l2,2c.2,.2,.45,.29,.71,.29s.51-.1,.71-.29Zm-16,0l2-2c.39-.39,.39-1.02,0-1.41s-1.02-.39-1.41,0l-2,2c-.39,.39-.39,1.02,0,1.41,.2,.2,.45,.29,.71,.29s.51-.1,.71-.29ZM18.71,6.71l2-2c.39-.39,.39-1.02,0-1.41s-1.02-.39-1.41,0l-2,2c-.39,.39-.39,1.02,0,1.41,.2,.2,.45,.29,.71,.29s.51-.1,.71-.29Z"/></svg>';
const ICON_MOND = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15,24a12.021,12.021,0,0,1-8.914-3.966,11.9,11.9,0,0,1-3.02-9.309A12.122,12.122,0,0,1,13.085.152a13.061,13.061,0,0,1,5.031.205,2.5,2.5,0,0,1,1.108,4.226c-4.56,4.166-4.164,10.644.807,14.41a2.5,2.5,0,0,1-.7,4.32A13.894,13.894,0,0,1,15,24Z"/></svg>';

const CSS = `
/* Profil-Badge unten links — identisch in allen Apps: Initialen des Users,
   gefüllter Kreis OHNE Rand. Farben über die App-Theme-Variablen (Radiology:
   --text-primary/--dark-bg, Study: --text-primary/--bg-primary): im Dark-
   Theme weiß mit schwarzen Initialen, im Light-Theme invertiert.
   Horizontal zentriert auf der Corner-Icon-Spalte (Inset + 2px + halbe
   1.6rem-Buttonbreite − halber 2rem-Badge). */
.profile-badge {
  position: fixed;
  bottom: 1rem;
  left: calc(1rem + 2px + 0.8rem - 1rem);
  width: 2rem; height: 2rem;
  display: flex; align-items: center; justify-content: center;
  border: none; border-radius: 50%;
  background: var(--text-primary, #fff);
  color: var(--bg-primary, var(--dark-bg, #000));
  font-family: 'PF Videotext', 'PFVideoText', sans-serif;
  font-size: 0.805rem; font-weight: 500; letter-spacing: 0.04em;
  /* Über dem Sidepanel (z 9999): das Badge bleibt auch bei expandiertem
     Panel unten links sichtbar und wird dort zur Profil-Zeile. */
  z-index: 10000;
  cursor: pointer; user-select: none;
}
/* Profilname rechts neben dem Badge — nur bei expandiertem Sidepanel
   sichtbar, auf derselben Label-Flucht wie die Aktions-Buttons darüber
   (Icon-Spalte 1.6rem + 1rem Gap ab Spalten-Inset 1rem + 2px). */
.profile-badge-name {
  position: fixed;
  bottom: 1rem;
  left: calc(1rem + 2px + 1.6rem + 1rem);
  height: 2rem;
  display: flex; align-items: center;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.9rem; font-weight: 500; color: #fff;
  white-space: nowrap;
  opacity: 0; pointer-events: none;
  transition: opacity 0.22s ease;
  z-index: 10000; cursor: pointer; user-select: none;
}
body.app-switch-open .profile-badge-name,
body.app-switcher-open .profile-badge-name { opacity: 1; pointer-events: auto; }
/* Ohne Initialen (nicht eingeloggt / kein Fallback) nicht anzeigen */
.profile-badge:empty { display: none; }
/* Optische Zentrierung, aus den PF-Videotext-Metriken berechnet: Versalien
   (0–658/1000 em) sitzen 0.126em unter der Line-Box-Mitte (Ascent 1160,
   Descent 250), Ink-Mitte 0.02em links der Box-Mitte (Sidebearings +
   trailing Letter-Spacing). */
.profile-badge span { display: inline-block; transform: translate(0.02em, -0.126em); }
/* Gleicher Look wie die übrigen Overlays (Einstellungen, Apps-Popup, …):
   #1a1a1a-Fläche, 12px-Radius, weicher Schatten, ohne Border */
.profile-panel {
  position: fixed; left: 1rem; bottom: calc(1rem + 2rem + 12px);
  width: 16rem; padding: 16px;
  display: none; flex-direction: column; gap: 12px;
  background: #1a1a1a; color: #fff;
  border: none; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 10003;
  font-family: 'Inter', system-ui, sans-serif;
}
.profile-panel.is-open { display: flex; }
.profile-panel__head { display: flex; align-items: center; gap: 12px; }
.profile-panel__avatar {
  flex-shrink: 0; width: 2.5rem; height: 2.5rem; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: #fff; color: #000;
  font-family: 'PF Videotext', 'PFVideoText', sans-serif;
  font-size: 1rem; letter-spacing: 0.04em; user-select: none;
}
.profile-panel__avatar span { display: inline-block; transform: translate(0.02em, -0.126em); }
.profile-panel__id { min-width: 0; display: flex; flex-direction: column; line-height: 1.3; }
.profile-panel__name {
  font-size: 0.95rem; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.profile-panel__sub { font-size: 0.75rem; color: #b0b0b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.profile-panel__divider { height: 1px; background: rgba(255, 255, 255, 0.1); }
/* Panel-Aktionen (Admin Einstellungen, Logout): gleiche Formsprache, gleiche
   Textkante. Das negative Margin plus gleich grosses Padding ist Absicht - die
   Hover-Flaeche darf bis an den Panelrand reichen, der TEXT soll aber buendig
   unter Name und Sektionen stehen. Mit blossem Padding von 8px 12px sassen beide
   um 12 px eingerueckt und tanzten aus der Reihe. */
/* Die Aktionen bilden einen Block: eng untereinander, ohne Trennlinie dazwischen. */
.profile-panel__aktionen { display: flex; flex-direction: column; gap: 2px; }
.profile-panel__aktion {
  display: flex; align-items: center;
  width: calc(100% + 24px); margin: 0 -12px; padding: 8px 12px;
  border: 1px solid transparent; border-radius: 10px; background: transparent;
  color: #fff; cursor: pointer; text-align: left; font-family: inherit;
  font-size: 0.9rem; font-weight: 400;
  transition: background 0.15s, border-color 0.15s;
}
.profile-panel__aktion:hover {
  background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.1);
}
/* Darstellung: eine Zeile mit Beschriftung links und Schalter rechts, auf
   derselben Textkante wie die Aktionen darunter. Der Schalter traegt Sonne
   und Mond; der Knopf liegt auf der gewaehlten Seite. Farben aus der festen
   Panel-Palette, damit er in jeder App gleich aussieht. */
.profile-panel__zeile {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 0.9rem;
}
.profile-panel__schalter {
  position: relative; flex-shrink: 0;
  width: 3.1rem; height: 1.6rem; padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18); border-radius: 999px;
  background: rgba(255, 255, 255, 0.08); color: #b0b0b0;
  cursor: pointer; transition: background 0.2s;
}
.profile-panel__schalter:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.profile-panel__schalter-icon {
  position: absolute; top: 50%; transform: translateY(-50%);
  display: flex; width: 0.8rem; height: 0.8rem;
}
.profile-panel__schalter-icon svg { width: 100%; height: 100%; }
.profile-panel__schalter-icon--sonne { left: 0.4rem; }
.profile-panel__schalter-icon--mond { right: 0.4rem; }
.profile-panel__schalter-knopf {
  position: absolute; top: 0.15rem; left: 0.15rem;
  width: 1.2rem; height: 1.2rem; border-radius: 50%;
  background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  transition: left 0.2s ease;
}
.profile-panel__schalter[aria-checked="true"] .profile-panel__schalter-knopf {
  left: calc(100% - 1.35rem);
}
@media (prefers-reduced-motion: reduce) {
  .profile-panel__schalter, .profile-panel__schalter-knopf { transition: none; }
}
/* Formular-Bausteine (Labels, Felder, Knöpfe): im Panel selbst nicht mehr
   benutzt, aber vom Konto-Tab und den Admin-Tabs der Einstellungen — die
   laufen nur aus initProfilePanel heraus, das dieses CSS vorher einhängt.
   Dezente Flächen, 8px-Radius, kleine Labels in Grau. */
.profile-panel__section { display: flex; flex-direction: column; gap: 6px; }
.profile-panel__label {
  font-size: 0.7rem; font-weight: 600; letter-spacing: 0.07em;
  text-transform: uppercase; color: #b0b0b0;
}
.profile-panel__input {
  width: 100%; padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 8px;
  background: rgba(255, 255, 255, 0.06); color: #fff;
  font-family: inherit; font-size: 0.85rem;
}
.profile-panel__input::placeholder { color: #8a8a8a; }
.profile-panel__input:focus { outline: none; border-color: rgba(255, 255, 255, 0.3); }
.profile-panel__btn {
  align-self: flex-start; padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px;
  background: transparent; color: #fff; cursor: pointer;
  font-family: inherit; font-size: 0.82rem; font-weight: 500;
  transition: background 0.15s, border-color 0.15s;
}
.profile-panel__btn:hover { background: rgba(255, 255, 255, 0.08); }
.profile-panel__keyrow { display: flex; gap: 6px; }
.profile-panel__keyrow .profile-panel__input { flex: 1; min-width: 0; }
.profile-panel__keyrow .profile-panel__btn { align-self: stretch; flex-shrink: 0; }
.profile-panel__status { font-size: 0.75rem; color: #b0b0b0; }
.profile-panel__status--err { color: #f0a5a5; }
.profile-panel__hint { font-size: 0.78rem; color: #b0b0b0; line-height: 1.4; }
.profile-panel__link {
  align-self: flex-start; border: none; background: transparent; padding: 0;
  color: #b0b0b0; text-decoration: underline; cursor: pointer;
  font-family: inherit; font-size: 0.75rem;
}
.profile-panel__link:hover { color: #fff; }
.profile-panel__link[hidden] { display: none; }

@media (hover: none) and (pointer: coarse) {
  /* Das Badge liest als letztes Element der Bodenleiste: rechte Kante,
     vertikal auf deren Mitte. Kein DOM-Umbau — nur Position. */
  .profile-badge {
    left: auto;
    right: 1rem;
    bottom: calc((var(--mednos-bottombar-h) - 2rem) / 2 + env(safe-area-inset-bottom, 0px));
  }
  /* Seit 12.09.2026 haengt app-switcher.js das Badge auf Touch in eine eigene
     Zelle der Bodenleiste, damit es mit den Knoepfen gleichmaessig verteilt
     wird. Dort steht es im Fluss statt fixed; die Regel oben bleibt der
     Rueckfall fuer eine App ohne .header-corner. */
  .app-switch-touch-cell > .profile-badge {
    position: static;
    flex-shrink: 0;
  }

  /* Das Namensschild sitzt am Desktop rechts NEBEN dem Badge, auf der
     Label-Flucht der Aktions-Buttons darueber (left: 1rem + 2px + 1.6rem +
     1rem). In einer Bodenleiste gibt es diese Flucht nicht — es stuende
     allein am linken Rand, weit weg vom Badge. */
  .profile-badge-name {
    display: none;
  }

  /* Das Panel faehrt weiterhin nach oben auf, jetzt aber ueber der Leiste
     und an der rechten Kante ausgerichtet. */
  .profile-panel {
    left: auto;
    right: 1rem;
    bottom: calc(var(--mednos-bottombar-h) + env(safe-area-inset-bottom, 0px) + 12px);
    width: min(16rem, calc(100vw - 2rem));
    max-height: calc(100dvh - var(--mednos-bottombar-h) - 4rem);
    overflow-y: auto;
  }
}
`;

function esc(s) {
  const d = document.createElement('div');
  d.textContent = s == null ? '' : s;
  return d.innerHTML;
}

// Initialen aus dem Benutzernamen: Großbuchstaben ("FelixGunzer" → "FG"),
// sonst erstes Zeichen.
export function initialsOf(name) {
  const caps = String(name || '').match(/[A-ZÄÖÜ]/g) || [String(name || '').charAt(0)];
  return caps.slice(0, 2).join('').toUpperCase();
}

function isProd() {
  return location.hostname === 'mednos.eu' || location.hostname.endsWith('.mednos.eu');
}

function darstellungHtml(theme) {
  return '<div class="profile-panel__zeile">' +
    '<span id="profilePanelDunkel">Dunkelmodus</span>' +
    `<button type="button" class="profile-panel__schalter" data-pp="theme" role="switch"` +
    ` aria-checked="${theme === 'dark'}" aria-labelledby="profilePanelDunkel">` +
    `<span class="profile-panel__schalter-icon profile-panel__schalter-icon--sonne">${ICON_SONNE}</span>` +
    `<span class="profile-panel__schalter-icon profile-panel__schalter-icon--mond">${ICON_MOND}</span>` +
    '<span class="profile-panel__schalter-knopf"></span>' +
    '</button></div>';
}

// Fallback-Loader: zentraler Auth-Server (nur in Produktion erreichbar).
// GET ohne Custom-Header — muss wie beim App-Switcher preflight-frei bleiben.
function defaultLoadUser() {
  if (!isProd()) return Promise.resolve(null);
  return fetch('https://mednos.eu/api/auth/me', { credentials: 'include' })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
}

function defaultLogout() {
  const done = () => { window.location.href = 'https://mednos.eu'; };
  if (!isProd()) { done(); return; }
  fetch('https://mednos.eu/api/auth/logout', { method: 'POST', credentials: 'include' })
    .then(done)
    .catch(done);
}

/**
 * @param {{
 *   badge?: string | HTMLElement,
 *   loadUser?: () => Promise<{ username?: string, role?: string, email?: string } | null>,
 *   onLogout?: () => void,
 *   darstellung?: boolean,
 * }} opts
 */
export function initProfilePanel(opts = {}) {
  const badge =
    typeof opts.badge === 'string'
      ? document.querySelector(opts.badge)
      : opts.badge || document.getElementById('profileBadge');
  if (!badge) return;

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  const panel = document.createElement('div');
  panel.className = 'profile-panel';
  document.body.appendChild(panel);

  const loadUser = opts.loadUser || defaultLoadUser;

  // Profilname neben dem Badge: erscheint, sobald das Sidepanel expandiert
  // ist (Body-Klasse), auf der Label-Flucht der Aktions-Buttons.
  const nameEl = document.createElement('span');
  nameEl.className = 'profile-badge-name';
  document.body.appendChild(nameEl);

  // Badge befüllen: Initialen im Span (für die optische Zentrierung).
  // Zuerst aus der vorhandenen Beschriftung (Fallback, z. B. hartkodiert
  // oder ohne Auth-Server), dann asynchron aus dem geladenen User.
  let cachedUser = null;
  function setBadge(name) {
    const span = document.createElement('span');
    span.textContent = initialsOf(name);
    badge.replaceChildren(span);
    badge.title = name;
    nameEl.textContent = name;
  }
  const fallbackName = badge.title || badge.textContent.trim();
  if (fallbackName) setBadge(fallbackName);
  loadUser().then((user) => {
    if (user?.username) {
      cachedUser = user;
      setBadge(user.username);
    }
  });

  // Lazy: Konsole und Kosten-Fenster entstehen erst, wenn ein Admin sie zum
  // ersten Mal aufruft — normale User bezahlen kein DOM dafür, und ein
  // Panel-Aufbau fasst keine Admin-Endpunkte an.
  let adminKonsole = null;
  let kostenFenster = null;

  function render(user) {
    // Fallback auf die Badge-Beschriftung, wenn kein User ladbar ist
    // (z. B. lokal ohne Auth-Server).
    const name = user?.username || badge.title || badge.textContent.trim();
    const sub = user?.email || user?.role || '';
    panel.innerHTML =
      '<div class="profile-panel__head">' +
      `<span class="profile-panel__avatar"><span>${esc(initialsOf(name))}</span></span>` +
      '<span class="profile-panel__id">' +
      `<span class="profile-panel__name">${esc(name)}</span>` +
      (sub ? `<span class="profile-panel__sub">${esc(sub)}</span>` : '') +
      '</span></div>' +
      (opts.darstellung ? darstellungHtml(aktuellesTheme()) : '') +
      // Aktionen als ein Block untereinander. Einstellungen (und für Admins
      // Kosten) werden nach dem Rendern davor gehaengt.
      '<div class="profile-panel__aktionen">' +
      '<button type="button" class="profile-panel__aktion" data-pp="logout">Logout</button>' +
      '</div>';
    panel.querySelector('[data-pp="logout"]').addEventListener('click', () => {
      (opts.onLogout || defaultLogout)();
    });
    // Der Schalter wirkt sofort und bleibt offen stehen — man sieht die neue
    // Darstellung, ohne das Panel neu zu oeffnen. Auch lokal (ohne Cookie).
    panel.querySelector('[data-pp="theme"]')?.addEventListener('click', (e) => {
      const dunkel = aktuellesTheme() !== 'dark';
      setzeTheme(dunkel ? 'dark' : 'light');
      e.currentTarget.setAttribute('aria-checked', String(dunkel));
    });

    // Die Einstellungen sprechen mednos.eu direkt an, also nur in Produktion;
    // lokal ist mednos.eu nicht erreichbar.
    //
    // Zusätzlich erst rendern, wenn `user` tatsächlich bekannt ist: open()
    // ruft bei kaltem Cache erst render(null) und danach render(user) auf.
    // Ohne dieses Gate würden die Einträge erst im zweiten Render sichtbar
    // aufpoppen, weil render(null) die Rolle noch nicht kennen kann.
    const aktionen = panel.querySelector('.profile-panel__aktionen');
    if (!isProd() || !user) return;
    const istAdmin = user.role === 'admin';

    // Ein Eintrag für alles Einstellbare: Konto (Passwort, API-Key) für jede
    // Person, dazu für Admins Benutzerverwaltung, Schlüssel und App-Wartung als
    // weitere Tabs. Das Panel ist 16 rem breit — jeder weitere Belang machte es
    // laenger, nicht uebersichtlicher (s. Spec 2026-08-24 admin-konsole-tabs).
    const einstellungenKnopf = document.createElement('button');
    einstellungenKnopf.type = 'button';
    einstellungenKnopf.className = 'profile-panel__aktion';
    einstellungenKnopf.textContent = istAdmin ? 'Admin Einstellungen' : 'Einstellungen';
    einstellungenKnopf.addEventListener('click', () => {
      if (!adminKonsole) adminKonsole = initAdminKonsole({ admin: istAdmin });
      close();
      adminKonsole.open();
    });

    if (istAdmin) {
      // Kosten stehen bewusst NEBEN der Konsole, nicht darin: Schluessel rotieren
      // und App-Wartung sind Eingriffe, die Kostenuebersicht ist ein Blick auf
      // Zahlen. Als Tab lag sie hinter zwei Klicks und unter einer Schluesselliste.
      const kostenKnopf = document.createElement('button');
      kostenKnopf.type = 'button';
      kostenKnopf.className = 'profile-panel__aktion';
      kostenKnopf.textContent = 'Kosten';
      kostenKnopf.addEventListener('click', () => {
        if (!kostenFenster) kostenFenster = initLlmCostsOverlay();
        close();
        kostenFenster.open();
      });

      aktionen.prepend(kostenKnopf);
    }
    // Zuletzt voran — Reihenfolge im Panel: (Admin) Einstellungen, Kosten, Logout.
    aktionen.prepend(einstellungenKnopf);
  }

  let isOpen = false;
  function open() {
    isOpen = true;
    if (cachedUser) {
      render(cachedUser);
    } else {
      render(null);
      loadUser().then((user) => {
        if (user) {
          cachedUser = user;
          render(user);
        }
      });
    }
    panel.classList.add('is-open');
  }
  function close() {
    isOpen = false;
    panel.classList.remove('is-open');
  }

  badge.setAttribute('role', 'button');
  badge.setAttribute('tabindex', '0');
  badge.style.cursor = 'pointer';
  badge.addEventListener('click', () => (isOpen ? close() : open()));
  nameEl.addEventListener('click', () => (isOpen ? close() : open()));
  badge.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      isOpen ? close() : open();
    }
  });
  document.addEventListener('click', (e) => {
    if (!isOpen) return;
    if (panel.contains(e.target) || badge.contains(e.target) || nameEl.contains(e.target)) return;
    close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  window.__mednosProfile = { open, close, isOpen: () => isOpen };
}
